import React, {useEffect, useState, useContext} from "react";
import {
    Box, Stack, Typography, IconButton, Button,
    Modal, Fade, TextField, FormControlLabel, Checkbox, Alert, CircularProgress
} from "@mui/material";
import {IconX, IconPlugConnected, IconCheck, IconAlertTriangle} from "@tabler/icons-react";
import {grey} from "@mui/material/colors";
import {Context} from "../../../../context/AppContext"

const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: "90%",
    maxWidth: 480,
    bgcolor: 'background.paper',
    boxShadow: 24,
    borderRadius: 2,
    p: 3,
};

const initServer = {
    name: "",
    endpoint: "",
    active: true,
    flow_uid: "",
    auth_token: "",
};
const isValidUrl = (url) => {
    try {
        new URL(url);
        return true;
    } catch {
        return false;
    }
};

export const AddMCPServerModal = ({open, onClose, onSave, defaultFlowUid = ""}) => {
    const [server, setServer] = useState({...initServer});
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [testResult, setTestResult] = useState(null);
    const {testMCPConnection} = useContext(Context);

    const onChange = (key, value) => {
        setServer(prev => ({...prev, [key]: value}));
        if (key === "endpoint" || key === "auth_token") setTestResult(null);
    };

    const handleClose = () => {
        setServer({...initServer});
        onClose();
        setTestResult(null);
    };
    useEffect(() => {
        if (open) {
            setServer({...initServer, flow_uid: defaultFlowUid});
        }
    }, [open, defaultFlowUid]);

    const handleSave = async () => {
        if (!server.name || !server.endpoint) return;
        const payload = {
            name: server.name,
            endpoint: server.endpoint,
            active: server.active,
            flow_uid: server.flow_uid || defaultFlowUid,
            auth_token: server.auth_token
        };
        try {
            setSaving(true);
            await onSave(payload);
            handleClose();
        } catch (e) {
            console.error("Failed to add MCP server:", e);
        } finally {
            setSaving(false);
        }
    };
    const handleTest = async () => {
        if (!server.endpoint) return;
        setTesting(true);
        setTestResult(null);
        const data = await testMCPConnection({
            endpoint: server.endpoint,
            auth_token: server.auth_token || null
        });
        setTestResult(data);
        setTesting(false);
    };


    return (
        <Modal open={open} onClose={handleClose}>
            <Fade in={open}>
                <Box sx={modalStyle}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{mb: 2}}>
                        <Typography variant="h6" fontWeight={600}>Add MCP Server</Typography>
                        <IconButton size="small" onClick={handleClose}>
                            <IconX size={18} color={grey[600]}/>
                        </IconButton>
                    </Stack>

                    <Stack gap={2}>
                        <TextField
                            size="small" label="Name" fullWidth autoFocus
                            placeholder="Enter server name"
                            value={server.name}
                            onChange={e => onChange("name", e.target.value)}
                        />
                        <TextField
                            size="small" label="Endpoint" fullWidth
                            placeholder="https://..."
                            value={server.endpoint}
                            onChange={e => onChange("endpoint", e.target.value)}
                            error={!!server.endpoint && !isValidUrl(server.endpoint)}
                            helperText={!!server.endpoint && !isValidUrl(server.endpoint) ? "Invalid URL" : ""}
                        />
                        <TextField
                            size="small" label="Auth Token" fullWidth
                            type="password"
                            placeholder="Enter auth token"
                            value={server.auth_token}
                            onChange={e => onChange("auth_token", e.target.value)}
                        />
                    </Stack>
                    <Box sx={{mt: 2}}>
                        <Button
                            color="secondary" size="small"
                            disabled={testing || !server.endpoint}
                            startIcon={testing ? <CircularProgress size={14}/> : <IconPlugConnected size={16}/>}
                            onClick={handleTest}
                            sx={{textTransform: "none", borderRadius: 2, border: "none"}}
                        >
                            {testing ? "Testing..." : "Test connection"}
                        </Button>

                        {testResult && (
                            <Alert
                                severity={testResult.ok ? "success" : "error"}
                                icon={testResult.ok ? <IconCheck size={18}/> : <IconAlertTriangle size={18}/>}
                                sx={{borderRadius: 0, fontSize: 13, mt: 1.5}}
                            >
                                {testResult.message}
                                {testResult.ok && testResult.server_name && (
                                    <Box component="span" sx={{display: "block", fontSize: 12, opacity: 0.8, mt: 0.5}}>
                                        {testResult.server_name}
                                        {testResult.protocol_version && ` · MCP ${testResult.protocol_version}`}
                                        {testResult.tool_count != null && ` · ${testResult.tool_count} tools`}
                                    </Box>
                                )}
                            </Alert>
                        )}
                    </Box>

                    <Stack direction="row" gap={1} justifyContent="flex-end" sx={{mt: 3}}>
                        <Button
                            variant="contained" color="secondary"
                            disabled={!server.name || !server.endpoint || saving || !testResult?.ok || !isValidUrl(server.endpoint)}
                            onClick={handleSave}
                            sx={{textTransform: "none", borderRadius: 2}}
                        >
                            {saving ? "Saving..." : "Save"}
                        </Button>
                    </Stack>
                </Box>
            </Fade>
        </Modal>
    );
};

export default AddMCPServerModal;